import React, { Component } from 'react';
import _debounce from 'lodash.debounce';
import _throttle from 'lodash.throttle';
import _isEqual from 'lodash.isequal';
import _uniqBy from 'lodash.uniqby';
import cx from 'classnames';



import { credentials } from '@shootsta/client-auth';
import { replaceBucketKeysWithUrlsInArray } from '@shootsta/client-aws-s3';

import Error from '../../Error';
import { AssetLibraryBulkActions as bulkActions } from '../../../common/constants';
import {
  updateAssetItem,
  mergeAssets,
  removeAsset,
  getDefaultAdvancedFilterValue,
  getStatusFilterText,
  getDescendantTags,
  lastSegment } from
'../utils';
import AssetList from './AssetsList';
import { TagSidebar } from './TagSidebar';
import CONFIRM_ACTION from './ConfirmationModal/constants/confirmActions';
import { chunkSize as CHUNK_SIZE, sortingOptions as SORTING_OPTIONS } from '../constants';


import '../styles/_library.scss';




































































const DEFAULT_SHOW_SORTING_DROPDOWN = true;
const DEFAULT_IS_LIST_EDITABLE = true;
const DEFAULT_SHOW_ACTIVE_FILTER = true;
const DEFAULT_WITH_BULK_ACTIONS = false;

const DEFAULT_SORT_BY_VALUE = SORTING_OPTIONS[0].value;
const DEFAULT_ADDED_BY_VALUE = [getDefaultAdvancedFilterValue('addedBy')];
const DEFAULT_DATE_UPLOADED_VALUE = [getDefaultAdvancedFilterValue('dateUploaded')];
const DEFAULT_FILE_TYPE_VALUE = [getDefaultAdvancedFilterValue('fileType')];
const DEFAULT_STATUS_VALUE = [getDefaultAdvancedFilterValue('status')];
const getNormalisedAddedBy = (addedBy) => {
  if (addedBy && addedBy.includes('ME') && !addedBy.includes('ALL')) {
    return [credentials.get().user.userId];
  }

  if (addedBy && addedBy.includes('ALL') && addedBy.length > 1) {
    const trimmedUser = [...addedBy];
    trimmedUser.splice(trimmedUser.indexOf('ALL'), 1);

    return trimmedUser;
  }

  return [...addedBy];
};

const getIsArchived = (status) => getStatusFilterText(status, DEFAULT_STATUS_VALUE) === 'ARCHIVED';

const getNewAssets = (assets, assetIds) => {
  let newAssets = _uniqBy([...(assets || [])], 'assetId');
  assetIds.forEach((assetId) => {
    newAssets = removeAsset(newAssets, assetId);
  });

  return newAssets;
};

const removeAssetsFromAssetsMap = (assetIds, removeFromAssetsMap) => {
  assetIds.forEach((assetId) => {
    removeFromAssetsMap(assetId);
  });
};

export default class AssetLibrary extends Component {












  static defaultProps = {
    isListEditable: DEFAULT_IS_LIST_EDITABLE,
    showActiveFilter: DEFAULT_SHOW_ACTIVE_FILTER,
    showSortingDropdown: DEFAULT_SHOW_SORTING_DROPDOWN,
    withBulkActions: DEFAULT_WITH_BULK_ACTIONS
  };

  constructor(props) {
    super(props);

    this.loadingMore = false;
    this._debouncedFilterChange = _debounce(this.handleFilterChange, 500);
    this._throttledNeedMore = _throttle(this.needMore, 500);

    this.state = {
      assets: [],
      users: [],
      dropZoneDisplayed: false,
      offset: 0,
      limit: CHUNK_SIZE * 2,
      showingNum: CHUNK_SIZE,
      totalResults: 0,
      loading: false,
      sortBy: DEFAULT_SORT_BY_VALUE,
      advancedFilters: {
        addedBy: DEFAULT_ADDED_BY_VALUE,
        dateUploaded: DEFAULT_DATE_UPLOADED_VALUE,
        fileType: DEFAULT_FILE_TYPE_VALUE,
        status: DEFAULT_STATUS_VALUE
      },
      selectedTags: [],
      allTags: [],
      assetTagMap: {},
      mobileFoldersOpen: false,
      isSidebarEditing: false
    };
  }

  // Filter by ONLY the selected folder(s) — not their nested subfolders. A
  // parent therefore lists just its own directly-tagged assets, mirroring the
  // gray direct-count badge in the sidebar. Drill into a subfolder to see its
  // assets. (The blue badge is an informational subtree rollup, not a filter.)
  // Returns undefined when nothing is selected (no filter).
  getEffectiveTagIds = () => {
    const { selectedTags } = this.state;
    return selectedTags.length ? [...selectedTags] : undefined;
  };

  handleSidebarEditingChange = (isEditing) => {
    this.setState({ isSidebarEditing: isEditing });
  };

  handleToggleMobileFolders = () => {
    this.setState(({ mobileFoldersOpen }) => ({ mobileFoldersOpen: !mobileFoldersOpen }));
  };

  handleCloseMobileFolders = () => {
    this.setState({ mobileFoldersOpen: false });
  };

  fetchAssets = () => {
    this.setState({ loading: true }, async () => {
      const { getAssetSummariesAndCount, filterByAssetType } = this.props;
      const {
        searchTerm: search,
        offset,
        limit,
        sortBy,
        advancedFilters: {
          addedBy,
          dateUploaded,
          fileType,
          status
        }
      } = this.state;

      const requestId = new Date().getTime();
      this._currentRequestId = requestId;

      // Overwrite fileType if filterByAssetType is passed
      const filterFileType = filterByAssetType || fileType;

      const { data = {}, error } = await getAssetSummariesAndCount({
        offset,
        limit,
        search,
        createdBy: getNormalisedAddedBy(addedBy),
        createdAt: dateUploaded,
        fileType: filterFileType,
        isArchived: getIsArchived(status),
        sortBy: sortBy ? [sortBy] : [DEFAULT_SORT_BY_VALUE],
        tagIds: this.getEffectiveTagIds()
      });

      // Ignore any but the most recent request
      if (requestId !== this._currentRequestId) {return;}

      if (error || !data) {
        return void this.setState({ loading: false, error });
      }

      const { getAssetSummariesAndCount: { count = 0, assets = [] } } = data;

      const withDummyTranscoded = assets.map((asset) => ({
        ...asset,
        assetFiles: {
          ...asset.assetFiles,
          SD: {},
          HD: {},
          STREAM: { fragments: [] }
        }
      }));

      // Ignore any but the most recent request
      if (requestId !== this._currentRequestId) {return;}

      // Build assetTagMap from the folders field on each asset
      const newTagMap = {};
      withDummyTranscoded.forEach((asset) => {
        newTagMap[asset.assetId] = (asset.folders || []).map((f) => f.tagId);
      });

      this.setState((prevState) => ({
        totalResults: count,
        assets: withDummyTranscoded,
        loading: false,
        offset: offset + prevState.limit,
        limit: CHUNK_SIZE,
        assetTagMap: { ...prevState.assetTagMap, ...newTagMap }
      }));
    });
  };

  fetchUsers = async () => {
    const { getUsers, apolloClient, organisationId } = this.props;

    try {
      const { data, error } = await getUsers();

      if (error || !data) {return void this.setState({ error });}

      const { getUsers: userData } = data;

      const users = await replaceBucketKeysWithUrlsInArray({
        apiClient: apolloClient,
        dataArray: userData.map(({ firstName, lastName, userId, imageUrl }) => ({
          label: `${firstName} ${lastName}`,
          value: userId,
          imageUrl
        })),
        organisationId,
        substitutedKeys: ['imageUrl']
      });

      this.setState({ users });
    } catch (error) {
      return void this.setState({ error });
    }
  };

  fetchAllTags = async () => {
    const { getTagCounts } = this.props;

    try {
      const { data = {}, error } = await getTagCounts();

      if (error || !data) {return;}

      const { getTagCountsForOrganisation: tags = [] } = data;

      this.setState({
        allTags: tags.sort((a, b) => a.label.localeCompare(b.label))
      });
    } catch (err) {

      // Silently fail — sidebar will just be empty
    }};

  handleTagSelect = (tag, isMulti) => {
    if (tag === '__ALL__') {
      this.setState(({ advancedFilters }) => ({
        selectedTags: [],
        offset: 0,
        limit: CHUNK_SIZE * 2,
        showingNum: CHUNK_SIZE,
        assets: [],
        loading: true,
        advancedFilters: {
          ...advancedFilters,
          addedBy: ['ALL']
        }
      }), () => this.fetchAssets());
      document.dispatchEvent(new CustomEvent('folder-selected', {
        detail: { label: 'All Assets', segment: 'All Assets' }
      }));
      return;
    }

    const { allTags } = this.state;
    const selectedTag = allTags.find((t) => t.tagId === tag);
    // `label` is the folder's full path; `segment` is its own name.
    const label = selectedTag ? selectedTag.label : 'All Assets';
    document.dispatchEvent(new CustomEvent('folder-selected', {
      detail: { label, segment: lastSegment(label) }
    }));

    this.setState(({ selectedTags, advancedFilters }) => {
      let newTags;
      if (isMulti) {
        newTags = selectedTags.includes(tag) ?
        selectedTags.filter((t) => t !== tag) :
        [...selectedTags, tag];
      } else {
        newTags = [tag];
      }

      return {
        selectedTags: newTags,
        offset: 0,
        limit: CHUNK_SIZE * 2,
        showingNum: CHUNK_SIZE,
        assets: [],
        loading: true,
        advancedFilters: {
          ...advancedFilters,
          addedBy: ['ALL']
        }
      };
    }, () => this.fetchAssets());
  };

  handleTagCreate = async (tagName) => {
    const { createAssetTag } = this.props;

    try {
      const { data, error } = await createAssetTag({ label: tagName });
      if (error || !data) {return;}

      const newTag = data.createTag;
      this.setState(({ allTags }) => ({
        allTags: [...allTags, { ...newTag, count: 0 }].
        sort((a, b) => a.label.localeCompare(b.label))
      }));
    } catch (err) {

      // Collision errors handled by the tags service
    }};

  handleTagRename = async (tagId, newLabel) => {
    const { updateAssetTag } = this.props;
    const { allTags } = this.state;

    const target = allTags.find((t) => t.tagId === tagId);
    if (!target) {return;}

    const oldLabel = target.label;

    // Renaming a folder rewrites its label AND cascades the prefix onto every
    // nested descendant (acme/2024 -> acme/2023 also moves acme/2024/final).
    const updates = getDescendantTags(allTags, oldLabel).map((t) => ({
      tagId: t.tagId,
      newLabel: t.label === oldLabel ? newLabel : `${newLabel}${t.label.slice(oldLabel.length)}`
    }));

    // Optimistic update (kept for rollback on failure).
    const snapshot = allTags;
    const relabelMap = new Map(updates.map((u) => [u.tagId, u.newLabel]));
    this.setState({
      allTags: allTags.
      map((t) => relabelMap.has(t.tagId) ? { ...t, label: relabelMap.get(t.tagId) } : t).
      sort((a, b) => a.label.localeCompare(b.label))
    });

    try {
      await Promise.all(updates.map((u) => updateAssetTag({ tagId: u.tagId, label: u.newLabel })));
    } catch (err) {
      this.setState({ allTags: snapshot });
      this.fetchAllTags();
    }
  };

  handleTagDelete = async (tagId) => {
    const { deleteAssetTag } = this.props;
    const { allTags } = this.state;

    // Deleting a folder deletes the whole subtree (folder + nested descendants)
    // and untags their assets. The assets themselves are never deleted.
    const target = allTags.find((t) => t.tagId === tagId);
    const ids = target ?
    getDescendantTags(allTags, target.label).map((t) => t.tagId) :
    [tagId];

    // Optimistic update
    this.setState(({ allTags: prev }) => ({
      allTags: prev.filter((t) => !ids.includes(t.tagId)),
      selectedTags: [],
      offset: 0,
      limit: CHUNK_SIZE * 2,
      showingNum: CHUNK_SIZE
    }));

    try {
      await Promise.all(ids.map((id) => deleteAssetTag({ tagId: id })));
    } catch (err) {

      // handled by re-fetch
    }
    this.setState({ assets: [], loading: true }, () => this.fetchAssets());
    this.fetchAllTags();
  };

  handleAssetTagToggled = (changes) => {
    // Optimistically update assetTagMap with the flushed changes
    if (changes && changes.length > 0) {
      this.setState(({ assetTagMap: prev }) => {
        const updated = { ...prev };
        changes.forEach(({ assetId, tagId, action }) => {
          const current = updated[assetId] || [];
          if (action === 'tag') {
            updated[assetId] = current.includes(tagId) ? current : [...current, tagId];
          } else {
            updated[assetId] = current.filter((id) => id !== tagId);
          }
        });
        return { assetTagMap: updated };
      });
    }
    // Refresh tag counts
    this.fetchAllTags();
  };

  handleDropAssetOnTag = async (tagId, assetIds) => {
    const { tagAssetResource } = this.props;
    const { allTags, assetTagMap } = this.state;

    const tag = allTags.find((t) => t.tagId === tagId);
    const tagLabel = tag ? tag.label : tagId;

    // Only assets that don't already carry the tag move the count. Dropping a
    // batch where some are already tagged must not bump the pill by the whole
    // batch — the delta rolls up into every ancestor's subtree total too.
    const newlyTagged = assetIds.filter(
      (assetId) => !(assetTagMap[assetId] || []).includes(tagId)
    );

    // Optimistically update assetTagMap (with dedup)
    this.setState(({ assetTagMap: prev }) => {
      const updated = { ...prev };
      assetIds.forEach((assetId) => {
        const current = updated[assetId] || [];
        if (!current.includes(tagId)) {
          updated[assetId] = [...current, tagId];
        }
      });
      return { assetTagMap: updated };
    });

    document.dispatchEvent(new CustomEvent('tag-drop-complete'));
    document.dispatchEvent(new CustomEvent('tag-animated', {
      detail: { tag: tagLabel, type: 'added' }
    }));
    if (newlyTagged.length) {
      document.dispatchEvent(new CustomEvent('tag-count-changed', {
        detail: { tag: tagLabel, delta: newlyTagged.length }
      }));
    }

    await Promise.all(
      assetIds.map((assetId) => tagAssetResource({ tagId, assetId }).catch(() => {}))
    );

    this.fetchAllTags();
  };

  componentDidMount = () => {
    this.fetchAssets();
    this.fetchUsers();
    this.fetchAllTags();
    document.addEventListener('toggle-mobile-folders', this.handleToggleMobileFolders);
  };

  componentWillUnmount = () => {
    document.removeEventListener('toggle-mobile-folders', this.handleToggleMobileFolders);
  };

  updateAssets = () => {
    const { loading } = this.state;
    if (loading) {
      return;
    }
    this.setState(
      {
        offset: 0,
        limit: CHUNK_SIZE * 2,
        showingNum: CHUNK_SIZE,
        sortBy: 'CREATED_AT DESC',
        assets: [],
        loading: true
      },
      () => {
        this.fetchAssets();
      }
    );
  };

  componentDidUpdate = (prevProps) => {
    const { assetsMap: prevAssetsMap, organisationId: prevOrganisationId } = prevProps;
    const { assetsMap, organisationId } = this.props;

    if (prevOrganisationId !== organisationId) {
      return void this.fetchAssets();
    }

    if (prevAssetsMap !== assetsMap && assetsMap && assetsMap.size > 0) {
      const hasNewAssets = Array.from(assetsMap.keys()).some((assetId) =>
      !prevAssetsMap || !prevAssetsMap.has(assetId)
      );

      const { loading } = this.state;
      if (hasNewAssets && !loading) {
        return void this.updateAssets();
      }
    }
  };

  setFilePickerRef = (ref) => {
    this._filePickerRef = ref;
  };

  handleUploadClick = () => {
    if (!this._filePickerRef) {
      return;
    }

    this._filePickerRef.openFileChooser();
  };

  handleManageAssetModalSave = (data) => {
    this.setState(({ assets: prevAssets }) => ({
      assets: updateAssetItem(prevAssets, data.updateAsset)
    }));
  };

  handleDeleteComplete = (assetId) => {
    const { removeFromAssetsMap } = this.props;

    this.setState(({ assets }) => ({
      assets: getNewAssets(assets, [assetId])
    }));

    removeAssetsFromAssetsMap([assetId], removeFromAssetsMap);

    // The server's per-tag counts drop when a tagged asset leaves the library,
    // so refetch — otherwise the sidebar pills stay stale until a remount.
    this.fetchAllTags();
  };

  handleAssetActionComplete = (assetId, confirmModalAction) => {
    const { removeFromAssetsMap } = this.props;

    if (Object.values(CONFIRM_ACTION).includes(confirmModalAction)) {
      this.setState(({ assets }) => ({
        assets: getNewAssets(assets, [assetId])
      }));

      removeAssetsFromAssetsMap([assetId], removeFromAssetsMap);

      // DELETE, ARCHIVE and UNARCHIVE can all move an asset in or out of the
      // server's tag counts. Refetch rather than guess a delta per action.
      this.fetchAllTags();
    }
  };

  handleBulkActionComplete = (assetIds, action) => {
    if ([bulkActions.DELETE, bulkActions.ARCHIVE, bulkActions.UNARCHIVE].includes(action)) {
      const { removeFromAssetsMap } = this.props;

      this.setState(({ assets }) => ({
        assets: getNewAssets(assets, assetIds)
      }));

      removeAssetsFromAssetsMap(assetIds, removeFromAssetsMap);
    }

    // Every bulk action shifts the server's tag counts: DELETE/ARCHIVE/UNARCHIVE
    // move assets out of (or back into) them, EDIT_TAGS retags in place.
    this.fetchAllTags();
  };

  handleFilterChange = ({ filters: { sortBy }, searchTerm }) => {
    const { searchTerm: currSearch, sortBy: currSortBy } = this.state;
    const newSortBy = sortBy || DEFAULT_SORT_BY_VALUE;

    if (currSearch === searchTerm && currSortBy === newSortBy) {
      return;
    }

    this.setState({
      offset: 0,
      limit: CHUNK_SIZE * 2,
      showingNum: CHUNK_SIZE,
      searchTerm,
      sortBy: newSortBy,
      assets: [],
      loading: true
    }, () => this.fetchAssets());
  };

  handleAdvancedFilterChange = ({
    addedBy = DEFAULT_ADDED_BY_VALUE,
    dateUploaded = DEFAULT_DATE_UPLOADED_VALUE,
    fileType = DEFAULT_FILE_TYPE_VALUE,
    status = DEFAULT_STATUS_VALUE
  }) => {
    const { advancedFilters: {
        addedBy: currAddedBy,
        dateUploaded: currDateUploaded,
        fileType: currFileType,
        status: currIsStatus
      } } = this.state;

    if (
    _isEqual(currAddedBy, addedBy) &&
    _isEqual(currDateUploaded, dateUploaded) &&
    _isEqual(currFileType, fileType) &&
    _isEqual(currIsStatus, status))
    {
      return;
    }

    this.setState({
      offset: 0,
      limit: CHUNK_SIZE * 2,
      showingNum: CHUNK_SIZE,
      advancedFilters: {
        addedBy,
        dateUploaded,
        fileType,
        status
      },
      assets: [],
      loading: true
    }, () => this.fetchAssets());
  };

  getFilteredAssets = () => {
    const { assets = [], searchTerm, advancedFilters: { status } } = this.state;

    if (!assets) {return [];}

    const filteredByArchivedStatus = assets.filter(({
      archivedAt
    }) => Boolean(archivedAt) === getIsArchived(status));

    let filtered = filteredByArchivedStatus;

    if (searchTerm) {
      filtered = filtered.filter((item) => Object.values(item).some((dataField) =>
      dataField && typeof dataField === 'string' &&
      dataField.toLowerCase().includes(searchTerm.toLowerCase())
      ));
    }

    return filtered;
  };

  handleDragOver = (e) => {
    e.preventDefault();
  };

  handleDragLeave = (e) => {
    if (!e || e.relatedTarget === null) {
      this.setState({ dropZoneDisplayed: false });
    }
  };

  handleDragEnter = (e) => {
    // Only show file upload drop zone for external file drags, not internal asset drags
    if (e && e.dataTransfer && e.dataTransfer.types && e.dataTransfer.types.includes('Files')) {
      this.setState({ dropZoneDisplayed: true });
    } else if (!e) {
      this.setState({ dropZoneDisplayed: true });
    }
  };

  closeDropZone = (e) => {
    // Only react to external file drops. Internal asset drags (e.g. dragging
    // a card to a non-droppable area) also bubble a drop event up here, and
    // wiping `assets` would clear the ui-kit List's internal selection state.
    if (!e || !e.dataTransfer || !e.dataTransfer.types || !e.dataTransfer.types.includes('Files')) {
      return;
    }
    e.preventDefault();
    this.setState({
      dropZoneDisplayed: false,
      offset: 0,
      limit: CHUNK_SIZE * 2,
      showingNum: CHUNK_SIZE,
      searchTerm: '',
      assets: [],
      loading: true,
      sortBy: DEFAULT_SORT_BY_VALUE
    }, () => this.fetchAssets());
  };

  showMore = () => {
    const { showingNum, totalResults } = this.state;

    if (this.loadingMore) {return;}

    if (showingNum >= totalResults) {return;}

    this.setState((prevState) => ({
      showingNum: prevState.showingNum + CHUNK_SIZE
    }));
  };

  loadMore = async () => {
    const {
      searchTerm: search,
      offset, limit,
      assets,
      totalResults,
      sortBy,
      advancedFilters: { addedBy, dateUploaded, fileType, status }
    } = this.state;

    if (this.loadingMore) {return;}

    if (assets && assets.length >= totalResults) {return;}

    this.loadingMore = true;

    const { assetsMap, getAssetSummariesAndCount, organisationId } = this.props;

    const { data = {}, error } = await getAssetSummariesAndCount({
      offset,
      limit,
      search,
      createdBy: getNormalisedAddedBy(addedBy),
      createdAt: dateUploaded,
      fileType,
      isArchived: getIsArchived(status),
      sortBy: sortBy ? [sortBy] : [DEFAULT_SORT_BY_VALUE],
      tagIds: this.getEffectiveTagIds()
    });

    if (error) {
      this.loadingMore = false;
      return void this.setState({ error });
    }

    const { getAssetSummariesAndCount: { count = 0, assets: newAssets = [] } } = data;

    const withDummyTranscoded = newAssets.map((asset) => ({
      ...asset,
      offset: offset + CHUNK_SIZE,
      totalResults: count,
      assetFiles: {
        ...asset.assetFiles,
        SD: {},
        HD: {},
        STREAM: { fragments: [] }
      }
    }));

    const mergedAssets = mergeAssets(assetsMap, withDummyTranscoded, organisationId);

    // Build assetTagMap for newly loaded assets
    const newTagMap = {};
    withDummyTranscoded.forEach((asset) => {
      newTagMap[asset.assetId] = (asset.folders || []).map((f) => f.tagId);
    });

    this.setState((prevState) => ({
      assets: [...(prevState.assets || []), ...mergedAssets],
      loading: false,
      offset: offset + prevState.limit,
      assetTagMap: { ...prevState.assetTagMap, ...newTagMap }
    }));

    this.loadingMore = false;
  };

  needMore = async () => {
    if (this.loadingMore) {setTimeout(this.needMore, 100);return;}

    this.showMore();
    this.loadMore();
  };

  removeDuplicateAssets = (assets) => {
    if (!assets) {return assets;}

    const seen = new Set();
    return assets.filter(({ assetId }) => {
      const duplicate = seen.has(assetId);
      seen.add(assetId);
      return !duplicate;
    });
  };

  setListRef = (ref) => {
    if (!ref) {return;}

    this._listRef = ref;
  };

  getListRef = () => this._listRef;

  render() {
    const {
      history,
      organisationName,
      deleteAsset,
      orgPermissions,
      listProps,
      withBulkActions,
      isProjectBrief,
      handleAddToProjectBriefClick,
      onBulkSelectAssets,
      selectedDrawerItemIds,
      isListEditable = DEFAULT_IS_LIST_EDITABLE,
      showActiveFilter = DEFAULT_SHOW_ACTIVE_FILTER,
      showSortingDropdown = DEFAULT_SHOW_SORTING_DROPDOWN,
      filterByAssetType,
      drawerOpen,
      tagAssetResource,
      untagAssetResource
    } = this.props;

    const {
      users,
      loading,
      dropZoneDisplayed,
      advancedFilters,
      error,
      totalResults,
      offset,
      showingNum,
      allTags,
      selectedTags,
      assetTagMap,
      mobileFoldersOpen,
      isSidebarEditing,
      assets: stateAssets
    } = this.state;

    if (error) {return <Error />;}

    const labels = selectedTags.
    map((tagId) => allTags.find((t) => t.tagId === tagId)?.label || tagId).
    join(', ');
    const searchInputPlaceholder = labels ?
    `Search ${labels.length > 13 ? `${labels.slice(0, 10).trimEnd()}...` : labels}` :
    undefined;

    // remove any potential duplicates from async loadMore and handleFilterChange
    const displayArray = this.removeDuplicateAssets(this.getFilteredAssets());

    // adjust for any assets that have been filtered out due to permissions
    const numRequested = Math.min(totalResults, offset);
    const numReceived = displayArray.length;
    const adjustment = numRequested - numReceived;

    return (
      <section
        className={cx('library', 'library--with-sidebar', { 'drawer-open': drawerOpen, 'library--project-brief': isProjectBrief })}
        aria-label="Asset Library"
        onDrop={this.closeDropZone}
        onDragLeave={this.handleDragLeave}
        onDragEnter={this.handleDragEnter}
        onDragOver={this.handleDragOver}>


        <TagSidebar
          tags={allTags}
          selectedTags={selectedTags}
          isAllAssetsActive={
            selectedTags.length === 0 &&
            Array.isArray(advancedFilters.addedBy) &&
            advancedFilters.addedBy.length === 1 &&
            advancedFilters.addedBy[0] === 'ALL'
          }
          onTagSelect={this.handleTagSelect}
          onTagCreate={this.handleTagCreate}
          onTagRename={this.handleTagRename}
          onTagDelete={this.handleTagDelete}
          onDropAsset={this.handleDropAssetOnTag}
          assets={stateAssets}
          assetTagMap={assetTagMap}
          mobileOpen={mobileFoldersOpen}
          onMobileClose={this.handleCloseMobileFolders}
          onEditingChange={this.handleSidebarEditingChange}
          />

        <div className="library__content" style={{ position: 'relative' }} ref={this.setListRef}>
          {isSidebarEditing &&
          <div
            className="library__edit-overlay"
            onClick={() => {
              // Trigger the edit confirm in TagSidebar by dispatching a custom event
              document.dispatchEvent(new CustomEvent('sidebar-exit-edit'));
            }}
            role="presentation" />}


          <AssetList
            organisationName={organisationName}
            assets={displayArray.slice(0, showingNum)}
            users={users}
            history={history}
            setFilePickerRef={this.setFilePickerRef}
            onFilterChange={this._debouncedFilterChange}
            onAdvancedFilterChange={this.handleAdvancedFilterChange}
            loading={loading}
            dropZoneDisplayed={dropZoneDisplayed}
            closeDropZone={this.handleDragLeave}
            archivedFilter={getStatusFilterText(advancedFilters.status, DEFAULT_STATUS_VALUE)}
            deleteAsset={deleteAsset}
            onDeleteComplete={this.handleDeleteComplete}
            onAssetActionComplete={this.handleAssetActionComplete}
            onBulkActionComplete={this.handleBulkActionComplete}
            onManageAssetModalSave={this.handleManageAssetModalSave}
            orgPermissions={orgPermissions}
            loadMoreResults={this._throttledNeedMore}
            totalResults={totalResults - adjustment}
            getListRef={this.getListRef}
            isListEditable={isListEditable}
            showActiveFilter={showActiveFilter}
            showSortingDropdown={showSortingDropdown}
            listProps={listProps}
            withBulkActions={withBulkActions}
            isProjectBrief={isProjectBrief}
            handleAddToProjectBriefClick={(asset) => handleAddToProjectBriefClick(asset)}
            onBulkSelectAssets={onBulkSelectAssets}
            selectedDrawerItemIds={selectedDrawerItemIds}
            filterByAssetType={filterByAssetType}
            allTags={allTags}
            assetTagMap={assetTagMap}
            tagAssetResource={tagAssetResource}
            untagAssetResource={untagAssetResource}
            onTagsChanged={this.fetchAllTags}
            onAssetTagToggled={this.handleAssetTagToggled}
            searchInputPlaceholder={searchInputPlaceholder}
            addedByDefault={advancedFilters.addedBy[0]}
            onToggleMobileFolders={this.handleToggleMobileFolders} />

        </div>
      </section>);

  }
}